import React, { useCallback, useEffect, useRef, useState } from 'react';
import './App.scss';
import { SiteContext } from './utils/contexts';
import { UserContext } from './utils/contexts';
import AppLoader from './shared/AppLoader';
import SitePage from './SitePage';
import Error from './shared/Error';
import { useParams, generatePath, Redirect } from 'react-router-dom';
import { API } from './utils/api';
import { tracking } from './utils/tracking';
import { auth } from './utils/auth';
import { withErrorBoundary } from './shared/ErrorFallback';
import { useSnackbar } from 'notistack';
import useScript from './utils/useScript';

function getToken() {
  return auth.getToken();
}

function findSite(user, siteId) {
  if (!siteId) {
    return user?.sites[0];
  }
  return user?.sites?.find((site) => site.id.toString() === siteId.toString());
}

const ONBOARDING_REDIRECT_KEY = 'onboarding_redirect_at';

// Users who haven't finished onboarding are sent back to it. If we already redirected a moment
// ago, let them in so a user without a backend session can't bounce between the two apps forever.
const redirectToOnboardingIfPending = (profile) => {
  if (!profile.onboarding_pending) { return false; }

  let lastRedirect = 0;
  try { lastRedirect = Number(sessionStorage.getItem(ONBOARDING_REDIRECT_KEY)) || 0; } catch (_) {}
  if (Date.now() - lastRedirect < 30000) { return false; }

  try { sessionStorage.setItem(ONBOARDING_REDIRECT_KEY, String(Date.now())); } catch (_) {}
  window.location.href = `${process.env.REACT_APP_AUTH_HOST}/users/onboarding`;
  return true;
};

function App() {
  const { siteId } = useParams();
  const [loading, setLoading] = useState(true);
  const [scriptLoading, setScriptLoading] = useState(true);
  const [error, setError] = useState();
  const [site, setSite] = useState();
  const [user, setUser] = useState();
  // Read once at mount, not on every render: this only needs the token that was present
  // when the page loaded. Re-reading the cookie live here made the profile-fetch effect's
  // dependency flap (e.g. once auth.clearSession() runs below) and re-fire in a tight loop,
  // hammering the API with repeated 401s (see DEV-895).
  const [token] = useState(getToken);
  const [previewScriptToken, setPreviewScriptToken] = useState();
  const { closeSnackbar } = useSnackbar();
  const lastSiteIdRef = useRef();

  const updateUser = (newUser) => {
    newUser.update = updateUser;
    setUser(newUser);
  };

  const updateSite = (newSite) => {
    newSite.update = updateSite;
    setSite(newSite);
  };

  useEffect(() => {
    const site = findSite(user, siteId);
    if (site) {
      site.update = updateSite;
      setSite(site);
      // Only clear toasts on an actual site switch, not every user-object refresh.
      if (lastSiteIdRef.current !== undefined && lastSiteIdRef.current !== site.id) {
        closeSnackbar();
      }
      lastSiteIdRef.current = site.id;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, siteId]);

  useEffect(() => {
    API.profile(token).then(([ok, response]) => {
      if (ok) {
        if (redirectToOnboardingIfPending(response)) { return; }
        response.update = updateUser;
        setUser(response);
      } else {
        setError(response);
      }
      setLoading(false);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    if (!Number(siteId)) { return; }
    setPreviewScriptToken(undefined);
    API.previewScriptToken(token, siteId).then(([ok, response]) => {
      if (ok) { setPreviewScriptToken(response.token); }
    });
  }, [siteId, token]);

  // A <script src> tag can't send an Authorization header, so a short-lived,
  // site-scoped token (not the full session token) travels as a query param
  // instead -- see SitesController#preview_script/#preview_script_token.
  const previewScriptUrl =
    previewScriptToken &&
    `${process.env.REACT_APP_PREVIEW_SCRIPT.replace(':site_id', siteId)}?token=${encodeURIComponent(previewScriptToken)}`;

  // Stable identity so useScript's effect only re-runs when the url itself changes,
  // not on every unrelated re-render (that was tearing down and reloading the script).
  const onPreviewScriptLoad = useCallback(() => setScriptLoading(false), []);

  useScript(previewScriptUrl, onPreviewScriptLoad, () => window.hellobar);

  if (user && siteId !== 'new') {
    if (!user.last_site_id) {
      return <Redirect to="/sites/new" />;
    } else if (!siteId || !findSite(user, siteId)) {
      const lastSiteUrl = window.location.pathname == '/upgrade' ? generatePath('/sites/:siteId/upgrade', {
        siteId: user.last_site_id,
      }) : generatePath('/sites/:siteId', {
        siteId: user.last_site_id,
      });
      return <Redirect to={lastSiteUrl} />;
    }
  }

  tracking.initialize(user, site);

  if (error && error?.status === 401) {
    // auth.handleUnauthorized() (api.js) already cleared the session and is hard-redirecting
    // to /login the moment the 401 was detected (DEV-895) — nothing further to do here.
    return null;
  }

  return (
    <UserContext.Provider value={user}>
      <SiteContext.Provider value={site}>
        <AppLoader visible={loading || scriptLoading} />
        {error && <Error error={error} />}
        {!loading && !scriptLoading && !error && <SitePage />}
      </SiteContext.Provider>
    </UserContext.Provider>
  );
}

export default withErrorBoundary(App);
